import { SupabaseClient } from '@supabase/supabase-js'
import { Database, Tables } from "@/supabase/types"
import { TablesInsert, TablesUpdate } from "@/supabase/types"
import { PermAction, PermOptions, TableWithPermissions } from '@/types/base/permissions'
import Authuser, { evaluatePermissions } from "@/types/base/authuser"
import { TableType } from "@/types/base/enums"
import { Role } from "@/types/base/authuser"
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'
import { getWorkspaceById } from './workspaces'

const logger = createIsomorphicLogger('db.groups')

export const getGroupById = async (supabase: SupabaseClient<Database>, groupId: string) => {
  const { data: group, error } = await supabase
    .from("groups")
    .select("*")
    .eq("id", groupId)
    .single()

  if (!group) {
    logError(logger, error, 'Failed to fetch Group', { groupId })
    throw new Error(error.message)
  }

  return group
}

export const getOptionalGroupById = async (supabase: SupabaseClient<Database>, groupId: string) => {
  const { data: group, error } = await supabase
    .from("groups")
    .select("*")
    .eq("id", groupId)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Group', { groupId })
    throw new Error(error.message)
  }

  return group
}

export const getOptionalGroupByName = async (supabase: SupabaseClient<Database>, name: string) => {
  const { data: group, error } = await supabase
    .from("groups")
    .select("*")
    .eq("name", name)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Group', { name })
    throw new Error(error.message)
  }

  return group
}

export const getTenantGroupsByTenantId = async (supabase: SupabaseClient<Database>, tenantId: string) => {
	if (tenantId === null) {
    return [];
  }

  const { data: groups, error } = await supabase
    .from("groups")
    .select("*")
    .eq("tenant_id", tenantId)

  if (!groups) {
    logError(logger, error, 'Failed to fetch tenant Groups', { tenantId })
    throw new Error(error.message)
  }

  return groups
}

export const getTenantGroupsByTenantIdWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<TableWithPermissions<'groups'>[]> => {
	const allGroups = await getTenantGroupsByTenantId(supabase, await authuser.getTenantId(supabase))
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: TableWithPermissions<'groups'>[] = []

	for (const group of allGroups) {
		const perms = await evaluatePermissions(supabase, authuser,
				{ type: "groups", context: "ANY", uuid: group.id, ws: workspace})

		result.push({ ...group, permissions: perms })
	}

  return result
}

export const getGroupWorkspacesByGroupId = async (supabase: SupabaseClient<Database>, groupId: string) => {
  const { data: group, error } = await supabase
    .from("groups")
    .select(
      `
      id,
      name,
      workspaces (*)
    `
    )
    .eq("id", groupId)
    .single()

  if (!group) {
    logError(logger, error, 'Failed to fetch Group workspaces', { groupId })
    throw new Error(error.message)
  }

  return group
}

export const getAllGroups = async (supabase: SupabaseClient<Database>) => {
  const { data: groups, error } = await supabase
    .from("groups")
    .select("*")

  if (!groups) {
    logError(logger, error, 'Failed to fetch all Groups')
    throw new Error(error.message)
  }

  return groups
}

export const createGroup = async (
	supabase: SupabaseClient<Database>,
  group: TablesInsert<"groups">,
  workspace: Tables<"workspaces">
) => {
  const { data: createdGroup, error } = await supabase
    .from("groups")
    .insert([group])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to create Group', { group: printObject(group) })
    throw new Error(error.message)
  }

  logger.info('Group created successfully', { group: printObject(createdGroup) })

  await createGroupWorkspace(supabase, {
    user_id: createdGroup.user_id,
    group_id: createdGroup.id,
    workspace_id: workspace.id
  })

  return createdGroup
}

export const createGroups = async (
	supabase: SupabaseClient<Database>,
  groups: TablesInsert<"groups">[],
  workspace: Tables<"workspaces">
) => {
  const { data: createdGroups, error } = await supabase
    .from("groups")
    .insert(groups)
    .select("*")

  if (error) {
    logError(logger, error, 'Failed to create Groups', { groups: printObject(groups) })
    throw new Error(error.message)
  }

  logger.info('Groups created successfully', { groups: printObject(createdGroups) })

  await createGroupWorkspaces(supabase,
    createdGroups.map(group => ({
      user_id: group.user_id,
      group_id: group.id,
	    workspace_id: workspace.id
    }))
  )

  return createdGroups
}

export const createGroupWorkspace = async (supabase: SupabaseClient<Database>, item: {
  user_id: string
  group_id: string
  workspace_id: string
}) => {
  const { data: createdGroupWorkspace, error } = await supabase
    .from("group_workspaces")
    .insert([item])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to create Groups workspace', { item: printObject(item) })
    throw new Error(error.message)
  }

  logger.info('Group workspace created successfully', { item: printObject(createdGroupWorkspace) })
  return createdGroupWorkspace
}

export const createGroupWorkspaces = async (
	supabase: SupabaseClient<Database>,
  items: { user_id: string; group_id: string; workspace_id: string }[]
) => {
  const { data: createdGroupWorkspaces, error } = await supabase
    .from("group_workspaces")
    .insert(items)
    .select("*")

  if (error) {
    logError(logger, error, 'Failed to create Group workspaces', { items: printObject(items) })
    throw new Error(error.message)
  }

  logger.info('Group workspaces created successfully', { items: printObject(createdGroupWorkspaces) })
  return createdGroupWorkspaces
}

export const updateGroup = async (
	supabase: SupabaseClient<Database>,
  groupId: string,
  group: TablesUpdate<"groups">
) => {
  const { data: updatedGroup, error } = await supabase
    .from("groups")
    .update(group)
    .eq("id", groupId)
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to update Group', { groupId, group: printObject(group) })
    throw new Error(error.message)
  }

  return updatedGroup
}

export const deleteGroup = async (supabase: SupabaseClient<Database>, groupId: string) => {
  const { error } = await supabase.from("groups").delete().eq("id", groupId)

  if (error) {
    logError(logger, error, 'Failed to delete Group', { groupId })
    throw new Error(error.message)
  }

  return true
}

export const deleteGroupWorkspace = async (
	supabase: SupabaseClient<Database>,
  groupId: string,
  workspaceId: string
) => {
  const { error } = await supabase
    .from("group_workspaces")
    .delete()
    .eq("group_id", groupId)
    .eq("workspace_id", workspaceId)

  if (error) {
    logError(logger, error, 'Failed to delete Group workspace', { groupId, workspaceId })
    throw new Error(error.message)
  }

  return true
}

//Groups can be viewed by users and above, otherwise they can be managed root or by admin in their own tenant space
export const groupCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	if (authuser.isAuthorizedForRole(Role.root)) {
		return true
	}

	if (options.context == 'IMPEXP' || options.context == 'Folder') {
		return authuser.isAuthorizedForRole(Role.admin)
	}

	switch (options.action) {
		case PermAction.view:
			return authuser?.isAuthorizedForRole(Role.user)
		case PermAction.select:
			switch (options.context) {
				case "Users":
					return authuser?.isAuthorizedForRole(Role.admin)
				default:
					if (options.uuid) {
						const group = await getGroupById(supabase, options.uuid)
						return !authuser.isGenerated(group.sharing)
					}
			}

			return false

		case PermAction.create:
		case PermAction.save:  //needed to always be there for admin since needed to update roles & users
			//also applies for folder context, no difference for create save
			return authuser?.isAuthorizedForRole(Role.admin)

		case PermAction.drag:
		case PermAction.edit:
		case PermAction.update:
		case PermAction.delete:
		default:
			switch (options.context) {
				case "Folders":
				case "ANY":  //for switcher
					return authuser?.isAuthorizedForRole(Role.admin)
				default:
					return isAdminNotGenerated(supabase, authuser, options)
			}
			break;
	}
}

const isAdminNotGenerated = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	if (options.uuid) {
		const group = await getGroupById(supabase, options.uuid)
		const authorized = await authuser.isAuthorizedForTable(supabase, TableType.group, options.uuid, Role.admin)
		if (authorized) {
			return !authuser.isGenerated(group.sharing)
		}
	}
	return false  //we need an id to confirm in this case
}